import { X } from 'lucide-react';
import { SeatMap } from './SeatMap';
// import { useState } from 'react';

interface SeatSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingData: {
    company: string;
    departureTime: string;
    arrivalTime: string;
    duration: string;
    price: number;
  };
  selectedSeats: string[];
  onSeatSelect: (seatNumber: string) => void;
  onConfirm: () => void;
}

export const SeatSelectionModal = ({
  isOpen,
  onClose,
  bookingData,
  selectedSeats,
  onSeatSelect,
  onConfirm,
}: SeatSelectionModalProps) => {
  if (!isOpen) return null;

  // 🛡️ Defensive Guards
  const safeBookingData = bookingData ?? {
    company: 'Sin nombre',
    departureTime: '--:--',
    arrivalTime: '--:--',
    duration: '0h',
    price: 0,
  };
  const safeSelectedSeats = selectedSeats ?? [];

  const totalPrice = safeBookingData.price * safeSelectedSeats.length;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
        onClick={onClose}
      />

      {/* Modal - Bottom Sheet */}
      <div className="fixed inset-x-0 bottom-0 z-50 lg:hidden bg-white rounded-t-[24px] shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[#E5E7EB] flex-shrink-0">
          <div>
            <h3 className="font-bold text-[#111827]">{safeBookingData.company}</h3>
            <p className="text-xs text-[#6B7280]">
              {safeBookingData.departureTime} - {safeBookingData.arrivalTime} • {safeBookingData.duration}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F7F8FA] hover:bg-[#E5E7EB] transition-colors"
          >
            <X className="w-5 h-5 text-[#111827]" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 pb-56">
          <SeatMap selectedSeats={safeSelectedSeats} onSeatSelect={onSeatSelect} />
        </div>

        {/* Footer - Fixed - Always visible */}
        <div className="absolute bottom-0 left-0 right-0 bg-white border-t-2 border-[#E5E7EB] shadow-[0_-4px_12px_rgba(0,0,0,0.1)]">
          {safeSelectedSeats.length > 0 ? (
            <div className="p-4">
              {/* Price and Seats Info */}
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-xs text-[#6B7280] mb-0.5">Total</p>
                  <p className="text-2xl font-bold text-[#111827]">
                    ${totalPrice.toLocaleString('es-MX')}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-[#6B7280] mb-0.5">
                    {safeSelectedSeats.length} {safeSelectedSeats.length === 1 ? 'Asiento' : 'Asientos'}
                  </p>
                  <div className="flex flex-wrap gap-1 justify-end">
                    {safeSelectedSeats.slice(0, 3).map((seat) => (
                      <span
                        key={seat}
                        className="px-2 py-0.5 bg-[#FFF5F0] text-[#FF6A2A] text-xs font-bold rounded-[6px] border border-[#FF6A2A]"
                      >
                        {seat}
                      </span>
                    ))}
                    {safeSelectedSeats.length > 3 && (
                      <span className="px-2 py-0.5 bg-[#F7F8FA] text-[#6B7280] text-xs font-semibold rounded-[6px]">
                        +{safeSelectedSeats.length - 3}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t border-[#E5E7EB] mb-3"></div>
              
              {/* Continue Button */}
              <button
                onClick={onConfirm}
                className="w-full py-3.5 bg-[#FF6A2A] hover:bg-[#C2410C] text-white font-semibold rounded-[12px] transition-colors text-base"
              >
                Continuar
              </button>
            </div>
          ) : (
            <div className="p-4">
              <div className="text-center py-2">
                <p className="text-sm text-[#6B7280] mb-3">
                  Selecciona al menos un asiento para continuar
                </p>
                <button
                  disabled
                  className="w-full py-3.5 bg-[#E5E7EB] text-[#9CA3AF] font-semibold rounded-[12px] cursor-not-allowed text-base"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

<style>{`
  @keyframes slide-up {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`}</style>
    </>
  );
};